import type { RoomParticipant } from "./meeting-store"
import type { GroupMemberView } from "@/contexts/chat/domain"

/** A monogram + display name for one attendee, resolved from the space members. */
export interface RosterFace {
  readonly key: string
  readonly name: string
  readonly monogram: string
  readonly isMe: boolean
  readonly muted: boolean
  readonly speaking: boolean
}

/**
 * Join Chime's roster (externalUserId = users.id) to the space members so we can
 * show real names + monograms. Chat has no avatar URLs, so the monogram (first
 * letter of the name) is the avatar. An attendee not in the member list (e.g. a
 * cross-org guest) falls back to "Guest".
 */
export function resolveRoster(
  participants: ReadonlyArray<RoomParticipant>,
  members: ReadonlyArray<GroupMemberView>,
): ReadonlyArray<RosterFace> {
  const byId = new Map(members.map((m) => [m.userId, m]))
  return participants.map((p) => {
    const member = byId.get(p.externalUserId)
    const name = member?.name ?? "Guest"
    return {
      key: p.attendeeId,
      name: member?.isMe ? `${name} (You)` : name,
      monogram: (name.charAt(0) || "?").toUpperCase(),
      isMe: member?.isMe ?? false,
      muted: p.muted,
      speaking: p.speaking,
    }
  })
}
